import { dehydrate } from '@tanstack/react-query'
import { getProps } from 'ssr/getProps'
import { mapsSortOptions, WORKSPACE_TAB } from 'ui-components'
import { getWorkspaceLink } from 'utils'
import { runWithAmplifyServerContext, serverClient } from 'web-amplify/server'

import { mapsPerPage } from '~workspace/constants/maps'
import {
  getWorkspaceByIdQuery,
  getWorkspaceByIdQueryKey
} from '~workspace/queries/useGetWorkspaceById'
import {
  getSearchMapsQueryKey,
  searchMapsQuery
} from '~workspace/queries/useSearchMaps'

type Query = {
  workspaceId?: string
  workspaceName?: string
  query?: string
  page?: string
  tab?: string
  tags?: string
  sortBy?: string
}

export const getServerSideProps = getProps(async (context, client, props) => {
  const {
    workspaceId,
    workspaceName,
    tab,
    query = '',
    tags = '',
    sortBy = mapsSortOptions[1]!.value
  } = context.query as Query

  const page = Number(context.query.page)
  const checkout = String(context.query?.checkout || '')

  if (!workspaceId || !workspaceName) {
    return {
      notFound: true
    }
  }

  if (!tab) {
    return {
      redirect: {
        destination: getWorkspaceLink({
          workspaceId,
          workspaceName,
          tab: WORKSPACE_TAB.MAPS,
          page: 1,
          checkout
        }),
        permanent: false
      }
    }
  }

  if (!workspaceId || !workspaceName) {
    return {
      notFound: true
    }
  }

  const [field, order] = sortBy.toString().split('|') as [string, string]
  const tagIds = tags?.toString().split(',').filter(Boolean) || []

  const searchMapsInput = {
    workspaceId,
    limit: mapsPerPage,
    offset: (Number(page) - 1) * mapsPerPage,
    query,
    tagIds,
    sortBy: { field, order } as any
  }

  let results
  try {
    results = await runWithAmplifyServerContext({
      nextServerContext: { request: context.req, response: context.res },
      operation: (ctx) =>
        Promise.all([
          serverClient.graphql(ctx, {
            query: searchMapsQuery,
            variables: searchMapsInput,
            ...(props?.authenticated
              ? { authMode: 'userPool', authToken: props.token! }
              : { authMode: 'apiKey' })
          }),

          serverClient.graphql(ctx, {
            query: getWorkspaceByIdQuery,
            variables: {
              id: workspaceId
            }
          })
        ])
    })
  } catch (error) {
    console.error(error)
  }

  const [
    {
      data: { searchMaps }
    },
    {
      data: { getWorkspaceById }
    }
  ] = results || ([] as any)

  await Promise.all([
    client.prefetchQuery({
      queryKey: getSearchMapsQueryKey(searchMapsInput),
      queryFn: () => searchMaps
    }),

    client.prefetchQuery({
      queryKey: getWorkspaceByIdQueryKey(workspaceId),
      queryFn: () => getWorkspaceById
    })
  ])

  const { total = 0 } = searchMaps as {
    total?: number
  }

  const hasNextPage = total > page * mapsPerPage

  return {
    props: {
      dehydratedState: dehydrate(client),
      page,
      hasNextPage
    }
  }
})
